import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Container } from '@/components/site/Section';
import { Reveal } from '@/components/site/Reveal';
import { Picture } from '@/components/site/Picture';
import { CONTACT } from '@/components/site/SiteFooter';
import { useCatalog } from '@/state/catalog';
import { formatNumber } from '@/lib/api';
import { IMAGES, productImage } from '@/lib/images';
import { cn } from '@/lib/utils';

/*
  Forsiden følger tekstmalen fra Hardanger Rock. Seks fotografier – hvert motiv kun én gang
  (responsive AVIF/WebP-varianter, se lib/images.js og scripts/build-images.py):
  hero            hjullaster i bruddet (oppskalert, liggende) + stående beskjæring til mobil
  bildebånd       knuseverket ved fjorden (oppskalert)
  levering        arbeidsskip ved kai
  om oss          gravemaskiner + big bags på kaia
  prosjekt        fjorden
  Typografi: vanlige, konkrete overskrifter. Monospace brukes bare på faktiske data (fraksjon, tall).
*/

const THUMB_SIZES = '56px';

const FACTS = [
  { n: '01', title: 'Masser til jobben', text: 'Knust sand, grus, pukk og stein i ulike størrelser. Se bruksområder og tonnpriser i produktoversikten.' },
  { n: '02', title: 'Dokumentert kvalitet', text: 'Finn ytelseserklæringer og prøveresultater for produktene våre.' },
  { n: '03', title: 'Direkte fra egen kai', text: 'Vi laster større leveranser rett på båt fra anlegget i Dimmelsvik.' },
  { n: '04', title: 'Henting eller levering', text: 'Hent selv, eller bestill transport med tippbil, båt eller kranbil med big bag.' },
];

const STEPS = [
  { n: '01', title: 'Velg produkt og mengde', text: 'Finn massene du trenger, og oppgi antall tonn. For produkter med oppgitt tonnpris ser du et prisestimat med en gang.' },
  { n: '02', title: 'Velg henting eller levering', text: 'Velg mellom henting på anlegget, tippbil, båt eller big bag med kranbil. Oppgi ønsket dato og leveringsadresse dersom vi skal levere.' },
  { n: '03', title: 'Vi bekrefter detaljene', text: 'Du får et ordrenummer med en gang. Vi bekrefter leveringsdato, frakt og totalpris på e-post eller telefon før fakturering.' },
];

const DELIVERY = [
  { id: 'baat', n: '01', title: 'Båt', text: 'For større mengder som skal fraktes sjøveien. Vi laster direkte fra egen kai i Dimmelsvik.' },
  { id: 'tippbil', n: '02', title: 'Tippbil', text: 'Løse masser levert til byggeplass, tomt eller annen adresse i regionen. Levering og adkomst avklares ved bestilling.' },
  { id: 'kranbil', n: '03', title: 'Big bag med kranbil', text: 'En praktisk løsning når du trenger mindre mengder eller vil holde massene samlet. Sekkene løftes på plass etter avtale, der adkomst og kranens rekkevidde tillater det.' },
  { id: 'hent', n: '04', title: 'Hent selv', text: 'Hent med egnet bil eller tilhenger på anlegget i Dimmelsvik. Vi laster for deg.' },
];

const DOCS = [
  { id: 'ce', title: 'Ytelseserklæringer og CE-dokumentasjon', text: 'Se produktenes deklarerte egenskaper og tilhørende dokumentasjon.', link: 'Se ytelseserklæringer', href: 'https://hardangerrock.no/ce-deklarasjoner-eksterne-tester-og-proveresultater/#ce-deklarasjoner' },
  { id: 'tester', title: 'Eksterne prøveresultater', text: 'Resultater fra testing av kornfordeling og materialegenskaper.', link: 'Se prøveresultater', href: 'https://hardangerrock.no/ce-deklarasjoner-eksterne-tester-og-proveresultater/#eksterne-tester-og-proveresultater' },
  { id: 'csr', title: 'Samfunnsansvar', text: 'Dokumentasjon knyttet til selskapets samfunnsansvar.', link: 'Se CSR-sertifikat', href: 'https://hardangerrock.no/ce-deklarasjoner-eksterne-tester-og-proveresultater/#crs-sertifikat' },
];

/* ---------- Delte byggeklosser (brukes også av produktsiden) ---------- */

export function CategoryTabs({ categories, value, onChange, testPrefix = 'category-tab' }) {
  const all = [{ id: 'alle', label: 'Alle' }, ...categories];
  return (
    <div role="tablist" aria-label="Produktkategori" className="inline-flex max-w-full gap-1 overflow-x-auto rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] p-1">
      {all.map((c) => {
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(c.id)}
            className={cn('h-9 shrink-0 rounded-[8px] px-4 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', active ? 'bg-white text-[var(--hr-heading)] shadow-[var(--hr-shadow-sm)]' : 'text-[var(--hr-muted)] hover:text-[var(--hr-heading)]')}
            data-testid={`${testPrefix}-${c.id}`}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

export function ProductGridSkeleton({ count = 8 }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true" data-testid="products-loading">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-[var(--hr-radius-lg)] border border-[var(--hr-hairline)]">
          <Skeleton className="aspect-[4/3] w-full rounded-none" />
          <div className="space-y-3 p-5">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-9 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Små hjelpere ---------- */

/** Rolig seksjonsmerke i vanlig skrift (ikke versaler, ikke sporing). */
function Kicker({ children, inverted = false }) {
  return <p className={cn('text-[14px] font-medium', inverted ? 'text-white/60' : 'text-[var(--hr-muted)]')}>{children}</p>;
}

function H2({ children, className, inverted = false }) {
  return <h2 className={cn('mt-3 text-balance font-display text-[clamp(30px,3.8vw,50px)] font-semibold leading-[1.04] tracking-[-0.025em]', inverted ? 'text-white' : 'text-[var(--site-ink)]', className)}>{children}</h2>;
}

function Lead({ children, className, inverted = false }) {
  return <p className={cn('mt-5 max-w-[58ch] text-[16px] leading-7 sm:text-[17px]', inverted ? 'text-white/75' : 'text-[var(--hr-muted)]', className)}>{children}</p>;
}

/** Nummerert liste med hårlinjer – tallet i vanlig skrift, dempet. */
function NumberedList({ items, inverted = false, testPrefix }) {
  return (
    <ol className={cn('divide-y border-y', inverted ? 'divide-white/15 border-white/15' : 'divide-[var(--site-line)] border-[var(--site-line)]')}>
      {items.map((it, i) => (
        <Reveal as="li" key={it.n} delay={Math.min(i + 1, 3)} className="grid grid-cols-[40px_1fr] gap-4 py-5 sm:grid-cols-[48px_1fr]" data-testid={testPrefix ? `${testPrefix}-${it.id ?? it.n}` : undefined}>
          <span className={cn('hr-tabular pt-[3px] text-[13px] font-medium', inverted ? 'text-[var(--site-orange)]' : 'text-[var(--hr-accent-text)]')}>{it.n}</span>
          <div>
            <h3 className={cn('font-display text-[18px] font-semibold leading-snug tracking-[-0.01em]', inverted ? 'text-white' : 'text-[var(--site-ink)]')}>{it.title}</h3>
            <p className={cn('mt-1.5 max-w-[56ch] text-[14.5px] leading-6', inverted ? 'text-white/65' : 'text-[var(--hr-muted)]')}>{it.text}</p>
          </div>
        </Reveal>
      ))}
    </ol>
  );
}

/** Diskret parallakse (kun transform). Faktor < 0.1 holder det rolig. */
function useParallax(ref, factor = 0.08, scale = 1.08) {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = (r.top + r.height / 2 - vh / 2) / vh;
      const y = Math.max(-1, Math.min(1, progress)) * -r.height * factor;
      el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(${scale})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, factor, scale]);
}

function isQuote(product) {
  return product.priceType === 'quote' || product.pricePerTonne === null || product.pricePerTonne === undefined;
}

function Price({ product, inverted = false, size = 'md' }) {
  if (isQuote(product)) {
    return (
      <span className={cn('inline-flex items-center gap-1 font-medium', inverted ? 'text-white' : 'text-[var(--site-ink)]', size === 'lg' ? 'text-[20px]' : 'text-[15px]')} data-testid={`price-${product.slug}`}>
        Be om tilbud
      </span>
    );
  }
  return (
    <span className={cn('hr-tabular inline-flex items-baseline gap-1.5', inverted ? 'text-white' : 'text-[var(--site-ink)]')} data-testid={`price-${product.slug}`}>
      <span className={cn('font-display font-semibold tracking-[-0.02em]', size === 'lg' ? 'text-[40px] leading-none' : 'text-[18px] sm:text-[20px]')}>{formatNumber(product.pricePerTonne)}</span>
      <span className={cn('text-[13px]', inverted ? 'text-white/60' : 'text-[var(--hr-muted)]')}>kr/t</span>
    </span>
  );
}

/* ---------- Produkter og priser: tabell med bildepanel ---------- */

function PriceRow({ product, active, onActivate }) {
  const quote = isQuote(product);
  return (
    <li>
      <Link
        to={`/produkter/${product.slug}`}
        onMouseEnter={() => onActivate(product.slug)}
        onFocus={() => onActivate(product.slug)}
        className={cn(
          'site-row group -mx-3 grid min-h-[76px] grid-cols-[56px_minmax(0,1fr)_auto_20px] items-center gap-x-3 rounded-[12px] px-3 py-3 sm:gap-x-4',
          'lg:min-h-[84px] lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.5fr)_auto_24px] lg:py-4',
          active && 'is-active',
        )}
        aria-current={active ? 'true' : undefined}
        data-testid={`price-row-${product.slug}`}
      >
        <Picture variant={productImage(product.image)} sizes={THUMB_SIZES} alt="" className="h-14 w-14 rounded-[10px] object-cover lg:hidden" />
        <span className="min-w-0">
          <span className="block font-display text-[17px] font-semibold leading-tight tracking-[-0.01em] text-[var(--site-ink)] sm:text-[18px] xl:text-[19px]">{product.name}</span>
          <span className="mt-1 block text-[13px] leading-5 text-[var(--hr-muted)] lg:hidden">{product.short}</span>
        </span>
        <span className="hidden text-[14px] leading-5 text-[var(--hr-muted)] lg:block">{product.short}</span>
        <span className="text-right">
          <Price product={product} />
          <span className="sr-only">{quote ? ' – åpne produkt og be om tilbud' : ' – se produkt og bestill'}</span>
        </span>
        <ChevronRight className="h-5 w-5 justify-self-end text-[var(--hr-muted-2)] transition-transform duration-200 group-hover:translate-x-0.5 lg:hidden" aria-hidden="true" />
        <ArrowUpRight className="site-row-arrow hidden h-5 w-5 justify-self-end text-[var(--site-ink)] lg:block" aria-hidden="true" />
      </Link>
    </li>
  );
}

function PriceList({ catalog }) {
  const [active, setActive] = useState(null);
  const groups = useMemo(
    () => catalog.categories.map((c) => ({ ...c, products: catalog.products.filter((p) => p.category === c.id) })).filter((g) => g.products.length),
    [catalog.categories, catalog.products],
  );
  const current = catalog.products.find((p) => p.slug === active) ?? catalog.products[0];

  /*
    Bildepanelet laster kun bilder som faktisk er vist (ikke alle 14 på én gang). Et nytt bilde tones inn
    først når det er ferdig lastet, mens det forrige blir stående – så panelet aldri blinker tomt.
  */
  const [mounted, setMounted] = useState(() => (current ? [current.slug] : []));
  const [loaded, setLoaded] = useState({});
  const [shown, setShown] = useState(current?.slug ?? null);
  useEffect(() => {
    if (!current) return;
    setMounted((m) => (m.includes(current.slug) ? m : [...m, current.slug]));
    if (loaded[current.slug]) setShown(current.slug);
  }, [current, loaded]);
  const onLoaded = (slug) => {
    setLoaded((l) => (l[slug] ? l : { ...l, [slug]: true }));
    if (slug === current?.slug) setShown(slug);
  };

  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
      <div className="hidden lg:col-span-5 lg:block">
        <div className="site-photo sticky top-24 overflow-hidden rounded-[16px] bg-[var(--site-sand-2)]" data-testid="price-list-visual">
          <div className="relative aspect-[4/5]">
            {catalog.products
              .filter((p) => mounted.includes(p.slug))
              .map((p) => (
                <Picture
                  key={p.slug}
                  variant={productImage(p.image)}
                  sizes="(min-width: 1024px) 40vw, 0px"
                  alt={p.name}
                  onLoad={() => onLoaded(p.slug)}
                  imgRef={(el) => {
                    if (el?.complete && el.naturalWidth > 0) onLoaded(p.slug);
                  }}
                  className={cn('site-swap h-full w-full object-cover', shown === p.slug && 'is-active')}
                />
              ))}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] bg-[linear-gradient(180deg,rgba(27,28,34,0)_0%,rgba(27,28,34,0.74)_100%)]" aria-hidden="true" />
            {current && (
              <div className="absolute inset-x-7 bottom-7 text-white" aria-live="polite">
                <div className="site-mono text-[12px] text-white/70">{current.fraction}</div>
                <div className="mt-2 flex items-end justify-between gap-6">
                  <div className="font-display text-[28px] font-semibold leading-[1.05] tracking-[-0.02em]" data-testid="price-list-visual-name">{current.name}</div>
                  <Price product={current} inverted size="lg" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        {groups.map((g, gi) => {
          const allQuote = g.products.every(isQuote);
          return (
            <Reveal key={g.id} delay={Math.min(gi, 3)} className={cn(gi > 0 && 'mt-10')}>
              <div className="flex items-baseline justify-between border-b border-[var(--site-line)] pb-3">
                <h3 className="font-display text-[16px] font-semibold text-[var(--site-ink)]">{g.label}</h3>
                <span className="text-[13px] text-[var(--hr-muted)]">{allQuote ? 'Pris på forespørsel' : `${g.products.length} ${g.products.length === 1 ? 'produkt' : 'produkter'}`}</span>
              </div>
              <ul className="divide-y divide-[var(--site-line)]" data-testid={`price-group-${g.id}`}>
                {g.products.map((p) => (
                  <PriceRow key={p.slug} product={p} active={current?.slug === p.slug} onActivate={setActive} />
                ))}
              </ul>
            </Reveal>
          );
        })}
        <div className="mt-10 space-y-3 border-t border-[var(--site-line)] pt-6 text-[14px] leading-6 text-[var(--hr-muted)]">
          <p>Bruksområdene er veiledende. Riktig masse velges ut fra kravene i prosjektet.</p>
          <p>Standardproduktene har oppgitte tonnpriser. For big bag, steinprodukter og større mengder gir vi tilbud på forespørsel. Frakt og endelig pris bekreftes ved bestilling.</p>
        </div>
        <Button asChild className="mt-6 h-12 rounded-[8px] bg-[var(--site-ink)] px-6 text-[15px] font-semibold text-white shadow-none hover:bg-[var(--site-ink-2)]">
          <Link to="/produkter" data-testid="landing-all-products-link">
            Se alle produkter og bestill <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function PriceListSkeleton() {
  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-12" aria-busy="true" data-testid="products-loading">
      <div className="hidden lg:col-span-5 lg:block">
        <Skeleton className="aspect-[4/5] w-full rounded-[16px]" />
      </div>
      <div className="space-y-4 lg:col-span-7">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-[var(--site-line)] pb-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Siden ---------- */

export default function LandingPage() {
  const catalog = useCatalog();
  const heroImg = useRef(null);
  const bandImg = useRef(null);
  useParallax(heroImg, 0.06, 1.0);
  useParallax(bandImg, 0.1, 1.12);

  useEffect(() => {
    document.title = 'Hardanger Rock – Pukk, grus og stein fra Dimmelsvik';
  }, []);

  return (
    <div className="site" data-testid="landing-page">
      {/* ---------- Første skjermbilde ---------- */}
      {/* Trekkes opp under toppfeltet (konstant høyde 64/72 px, ingen hårlinje), så det aldri blir en lys stripe øverst. */}
      <section className="relative isolate -mt-16 flex min-h-[100svh] flex-col justify-end overflow-hidden bg-[var(--site-ink)] text-white sm:-mt-[72px]" data-testid="hero">
        <div className="site-photo absolute inset-0 -z-10" data-testid="hero-image">
          <Picture
            variant={IMAGES.hero}
            portrait={IMAGES.heroPortrait}
            sizes="100vw"
            priority
            imgRef={heroImg}
            alt="Hjullaster i steinbruddet til Hardanger Rock i Dimmelsvik."
            className="site-hero-img h-full w-full object-cover"
          />
          {/* Svak, jevn demping over hele bildet (desktop) + mørkere felt nederst der teksten ligger. Mobil får en kraftigere variant. */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(27,28,34,0.5)_0%,rgba(27,28,34,0.22)_30%,rgba(27,28,34,0.34)_55%,rgba(27,28,34,0.9)_100%)]" aria-hidden="true" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(27,28,34,0.12)_0%,rgba(27,28,34,0.35)_35%,rgba(27,28,34,0.82)_60%,rgba(27,28,34,0.95)_100%)] md:hidden" aria-hidden="true" />
        </div>

        <Container className="relative pb-8 pt-40 sm:pb-10">
          <div className="max-w-[1040px]">
            <h1 className="font-display text-[clamp(44px,8vw,120px)] font-bold leading-[0.94] tracking-[-0.035em] text-white" data-testid="hero-title">
              <span className="site-line-mask">
                <span>Steingoe på stein.</span>
              </span>
            </h1>
            <div className="site-fade site-delay-3 mt-8 flex flex-col gap-6 md:flex-row md:items-end md:gap-12">
              <p className="max-w-[46ch] text-[16px] leading-7 text-white sm:text-[17px] sm:text-white/90">Pukk, grus, knust sand og stein fra eget brudd ved Hardangerfjorden. Hent på anlegget, eller få massene levert med båt, tippbil eller i big bag med kranbil.</p>
              <div className="flex flex-wrap gap-3 md:shrink-0 md:pb-1">
                <Button asChild className="h-12 rounded-[8px] bg-[var(--site-orange)] px-6 text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)]">
                  <Link to="/produkter" data-testid="hero-cta-products">
                    Se produkter og priser <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-12 rounded-[8px] border-white/50 bg-transparent px-6 text-[15px] font-semibold text-white shadow-none hover:border-white hover:bg-white/10 hover:text-white">
                  <Link to="/prosjekt" data-testid="hero-cta-project">
                    Be om tilbud
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="site-fade site-delay-4 mt-12 flex items-center justify-between gap-6 border-t border-white/25 pt-5 text-[13.5px] text-white/75 sm:mt-14" data-testid="hero-footer">
            <span>{CONTACT.plant.address}</span>
            <a href={CONTACT.plant.phoneHref} className="hr-tabular shrink-0 whitespace-nowrap rounded-sm font-medium text-white/90 transition-colors duration-150 hover:text-white" data-testid="hero-phone">
              {CONTACT.plant.phone}
            </a>
          </div>
        </Container>
      </section>

      {/* ---------- Introduksjon ---------- */}
      <section className="bg-[var(--site-sand)] py-20 sm:py-28" data-testid="intro">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-6">
              <h2 className="text-balance font-display text-[clamp(30px,3.8vw,50px)] font-semibold leading-[1.04] tracking-[-0.025em] text-[var(--site-ink)]" data-testid="intro-title">
                <span className="lg:block">Eget brudd.</span> <span className="lg:block">Eget knuseverk.</span> <span className="lg:block">Egen kai.</span>
              </h2>
              <p className="mt-6 max-w-[56ch] text-[17px] leading-8 text-[var(--hr-text)] sm:text-[18px]">Hos Hardanger Rock foregår produksjon, sortering og lasting på samme anlegg. Fra Dimmelsvik leverer vi masser til gårdsplasser, veier, byggeplasser og større anleggsprosjekter – fra et hengerlass til en skipslast.</p>
            </Reveal>
            <div className="lg:col-span-5 lg:col-start-8" data-testid="facts-list">
              <NumberedList items={FACTS} />
            </div>
          </div>
        </Container>
      </section>

      {/* ---------- Produkter og priser ---------- */}
      <section id="prisliste" className="scroll-mt-20 bg-white py-20 sm:py-28" data-testid="products-section">
        <div id="produkter" className="sr-only" aria-hidden="true" />
        <Container>
          <Reveal className="grid gap-6 border-b border-[var(--site-line)] pb-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Kicker>Produkter og priser</Kicker>
              <H2>Finn massene du trenger.</H2>
              <Lead>Se produkter, bruksområder og priser nedenfor. Er du usikker på hvilken masse du skal velge, hjelper vi deg.</Lead>
            </div>
            <p className="text-[13.5px] leading-6 text-[var(--hr-muted)] lg:col-span-4 lg:col-start-9 lg:pb-1 lg:text-right">
              Priser per tonn, eks. mva. · Betalingsfrist 10 dager · Frakt avklares ved bestilling
            </p>
          </Reveal>
          <div className="mt-10">
            {catalog.status === 'loading' && <PriceListSkeleton />}
            {catalog.status === 'error' && (
              <div className="flex flex-col items-start gap-3 border-y border-[var(--site-line)] py-8" data-testid="products-error">
                <p className="font-display text-[20px] font-semibold text-[var(--site-ink)]">Kunne ikke hente produktene</p>
                <p className="text-[14px] text-[var(--hr-muted)]">{catalog.error}</p>
                <Button variant="outline" className="rounded-[8px]" onClick={catalog.reload} data-testid="products-retry">
                  Prøv igjen
                </Button>
              </div>
            )}
            {catalog.status === 'ready' && <PriceList catalog={catalog} />}
          </div>
        </Container>
      </section>

      {/* ---------- Bildebånd ---------- */}
      <section className="site-photo relative h-[62vh] min-h-[380px] overflow-hidden bg-[var(--site-ink)]" data-testid="photo-band">
        <Picture variant={IMAGES.knuseverk} sizes="100vw" imgRef={bandImg} alt="Knuseverk, transportbånd og lager med steinmasser ved Hardangerfjorden." className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: '50% 55%', transform: 'scale(1.12)' }} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(180deg,rgba(27,28,34,0)_0%,rgba(27,28,34,0.6)_100%)]" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0">
          <Container className="pb-7 text-[13.5px] text-white/85">Knusing og sortering på anlegget i Dimmelsvik.</Container>
        </div>
      </section>

      {/* ---------- Bestilling ---------- */}
      <section className="bg-[var(--site-sand)] py-20 sm:py-28" data-testid="how-it-works">
        <Container>
          <Reveal className="grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <Kicker>Bestilling</Kicker>
              <H2>Slik bestiller du.</H2>
              <Lead>Velg produkt, mengde og leveringsmåte. Vi går gjennom bestillingen og bekrefter detaljene med deg.</Lead>
            </div>
            <div className="lg:col-span-4 lg:flex lg:justify-end lg:pb-1">
              <Button asChild className="h-12 w-fit rounded-[8px] bg-[var(--site-ink)] px-6 text-[15px] font-semibold text-white shadow-none hover:bg-[var(--site-ink-2)]">
                <Link to="/produkter" data-testid="how-cta">
                  Start bestilling <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </Reveal>
          <ol className="mt-12 grid gap-10 border-t border-[var(--site-line)] pt-10 md:grid-cols-3 md:gap-0">
            {STEPS.map((s, i) => (
              <Reveal as="li" key={s.n} delay={i + 1} className={cn('md:pr-10', i > 0 && 'md:border-l md:border-[var(--site-line)] md:pl-10')} data-testid={`step-${s.n}`}>
                <span className="hr-tabular text-[13px] font-medium text-[var(--hr-accent-text)]">{s.n}</span>
                <h3 className="mt-4 font-display text-[21px] font-semibold leading-snug tracking-[-0.015em] text-[var(--site-ink)] sm:text-[23px]">{s.title}</h3>
                <p className="mt-3 text-[15px] leading-7 text-[var(--hr-muted)]">{s.text}</p>
              </Reveal>
            ))}
          </ol>
        </Container>
      </section>

      {/* ---------- Levering ---------- */}
      <section id="levering" className="scroll-mt-20 bg-[var(--site-ink)] text-white" data-testid="delivery-section">
        <div className="grid lg:grid-cols-2">
          <div className="px-5 py-20 sm:px-8 sm:py-28 lg:py-32 lg:pl-[max(2.5rem,calc((100vw-1280px)/2+2.5rem))] lg:pr-16">
            <Reveal>
              <Kicker inverted>Levering</Kicker>
              <H2 inverted>Hent hos oss. Eller få massene levert.</H2>
              <Lead inverted>Mengde, avstand og adkomst avgjør hvilken transport som passer best. Velg ønsket leveringsmåte når du bestiller, så avklarer vi frakt og tidspunkt.</Lead>
            </Reveal>
            <div className="mt-12">
              <NumberedList items={DELIVERY} inverted testPrefix="delivery" />
            </div>
          </div>
          <div className="site-photo relative min-h-[360px] lg:min-h-0">
            <Picture variant={IMAGES.arbeidsskip} sizes="(min-width: 1024px) 50vw, 100vw" alt="Lasteskip ved Hardanger Rocks kai under lasting av steinmasser." className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: '55% 50%' }} />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(180deg,rgba(27,28,34,0)_0%,rgba(27,28,34,0.7)_100%)]" aria-hidden="true" />
            <div className="absolute bottom-6 left-6 text-[13.5px] text-white/85 sm:bottom-8 sm:left-8">Lasting direkte fra egen kai ved Hardangerfjorden.</div>
          </div>
        </div>
      </section>

      {/* ---------- Kvalitet og dokumentasjon ---------- */}
      <section id="dokumentasjon" className="scroll-mt-20 bg-white py-20 sm:py-28" data-testid="quality-section">
        <div id="kvalitet" className="sr-only" aria-hidden="true" />
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker>Kvalitet og dokumentasjon</Kicker>
              <H2>Dokumentasjonen du trenger.</H2>
              <Lead>Her finner du produktdokumentasjon og eksterne prøveresultater. Trenger du dokumentasjon til et bestemt produkt eller prosjekt, hjelper vi deg å finne frem.</Lead>
            </Reveal>
            <ul className="divide-y divide-[var(--site-line)] border-y border-[var(--site-line)] lg:col-span-7">
              {DOCS.map((d, i) => (
                <Reveal as="li" key={d.id} delay={i + 1} className="grid gap-3 py-6 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-8" data-testid={`doc-${d.id}`}>
                  <div>
                    <h3 className="font-display text-[20px] font-semibold leading-snug tracking-[-0.015em] text-[var(--site-ink)]">{d.title}</h3>
                    <p className="mt-1.5 max-w-[56ch] text-[14.5px] leading-6 text-[var(--hr-muted)]">{d.text}</p>
                  </div>
                  <a href={d.href} target="_blank" rel="noreferrer" className="site-ul inline-flex w-fit items-center gap-1.5 text-[15px] font-semibold text-[var(--site-ink)]" data-testid={`doc-link-${d.id}`}>
                    {d.link} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                </Reveal>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ---------- Om Hardanger Rock ---------- */}
      <section id="om-oss" className="scroll-mt-20 bg-[var(--site-sand)] py-20 sm:py-28 lg:py-32" data-testid="about-section">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="grid grid-cols-[1.25fr_1fr] items-end gap-4 lg:col-span-7">
              <Reveal className="site-photo overflow-hidden rounded-[16px]">
                <Picture variant={IMAGES.gravemaskiner} sizes="(min-width: 1024px) 32vw, 55vw" alt="To gravemaskiner håndterer sprengstein i steinbruddet." className="aspect-[4/5] w-full object-cover" />
              </Reveal>
              <Reveal delay={1} className="site-photo mb-10 overflow-hidden rounded-[16px]">
                <Picture variant={IMAGES.bigbags} sizes="(min-width: 1024px) 24vw, 42vw" alt="Big bags med Hardanger Rock-logo på kaia, klare for levering." className="aspect-square w-full object-cover" style={{ objectPosition: '50% 65%' }} />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal>
                <Kicker>Om Hardanger Rock</Kicker>
                <H2>Stein fra Dimmelsvik. Til prosjekter nær og langt unna.</H2>
              </Reveal>
              <Reveal delay={1} className="mt-6 space-y-5 text-[15.5px] leading-7 text-[var(--hr-text)]">
                <p>Hardanger Rock produserer og leverer pukk, grus, knust sand og stein med base i Dimmelsvik i Kvinnherad. Kundene våre er privatpersoner, entreprenører, bygg- og anleggsbedrifter og offentlige virksomheter.</p>
                <p>Med eget brudd, knuseverk og kai håndterer vi både mindre hentelass og større leveranser med bil og båt. Vi hjelper deg med å avklare produkt, mengde og transport, enten du skal ordne gårdsplassen eller i gang med et større anleggsprosjekt.</p>
              </Reveal>
              <Reveal delay={2} className="mt-8">
                <a href="https://hardangerrock.no/om-oss/" target="_blank" rel="noreferrer" className="site-ul inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--site-ink)]" data-testid="about-link">
                  Mer om Hardanger Rock <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>

      {/* ---------- Prosjektpris ---------- */}
      <section className="site-photo relative isolate overflow-hidden bg-[var(--site-ink)] text-white" data-testid="project-cta">
        <Picture variant={IMAGES.fjordWide} portrait={IMAGES.fjordSquare} sizes="100vw" alt="" aria-hidden="true" className="absolute inset-0 -z-20 h-full w-full object-cover" style={{ objectPosition: '50% 40%' }} />
        <div className="absolute inset-0 -z-10 bg-[rgba(27,28,34,0.8)]" aria-hidden="true" />
        <Container className="grid gap-10 py-24 sm:py-32 lg:grid-cols-12 lg:items-end lg:gap-16 lg:py-36">
          <Reveal className="lg:col-span-8">
            <Kicker inverted>Prosjektpris</Kicker>
            <H2 inverted>Større mengder? Be om et samlet tilbud.</H2>
            <div className="mt-6 max-w-[62ch] space-y-4 text-[16px] leading-7 text-white/75 sm:text-[17px]">
              <p>Skal du opparbeide en tomt, bygge vei, sikre en elvekant eller anlegge en molo? Fortell oss hva du skal gjøre, hvor massene skal leveres og omtrent hvor mye du trenger.</p>
              <p>Vi kommer tilbake med et tilbud på masser og transport, og avklarer en leveringsplan som passer prosjektet. Vi gir også tilbud på plastringsstein, murestein og sprengt røys.</p>
            </div>
          </Reveal>
          <Reveal delay={1} className="lg:col-span-4 lg:flex lg:justify-end">
            <Button asChild className="h-12 shrink-0 rounded-[8px] bg-[var(--site-orange)] px-6 text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)]">
              <Link to="/prosjekt" data-testid="project-cta-button">
                Be om tilbud <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </Reveal>
        </Container>
      </section>

      {/* ---------- Kontakt ---------- */}
      <section id="kontakt" className="scroll-mt-20 bg-white py-20 sm:py-28" data-testid="contact-section">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker>Kontakt</Kicker>
              <H2>Spør oss om masser og levering.</H2>
              <Lead>Trenger du hjelp til å velge produkt, planlegge transport eller finne dokumentasjon? Ring anlegget eller send oss en e-post.</Lead>
              <div className="mt-10 space-y-3">
                <a href={CONTACT.plant.phoneHref} className="hr-tabular site-ul inline-block font-display text-[30px] font-semibold tracking-[-0.025em] text-[var(--site-ink)] sm:text-[38px]" data-testid="contact-phone">
                  {CONTACT.plant.phone}
                </a>
                <br />
                <a href={`mailto:${CONTACT.plant.email}`} className="site-ul inline-block font-display text-[20px] font-medium tracking-[-0.01em] text-[var(--site-ink)] sm:text-[23px]" data-testid="contact-email">
                  {CONTACT.plant.email}
                </a>
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <dl className="divide-y divide-[var(--site-line)] border-y border-[var(--site-line)]">
                <Reveal as="div" delay={1} className="grid gap-2 py-6 sm:grid-cols-[200px_1fr]" data-testid="contact-plant">
                  <dt className="text-[14px] font-medium text-[var(--hr-muted)]">{CONTACT.plant.label}</dt>
                  <dd>
                    <div className="text-[16px] text-[var(--site-ink)]">
                      {CONTACT.plant.street}
                      <br />
                      {CONTACT.plant.postal}
                    </div>
                    <a href="https://maps.google.com/?q=Omviksdalvegen+415,+5464+Dimmelsvik" target="_blank" rel="noreferrer" className="site-ul mt-2 inline-flex items-center gap-1 text-[14px] font-medium text-[var(--site-ink)]">
                      Vis i kart <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </dd>
                </Reveal>
                <Reveal as="div" delay={2} className="grid gap-2 py-6 sm:grid-cols-[200px_1fr]" data-testid="contact-admin">
                  <dt className="text-[14px] font-medium text-[var(--hr-muted)]">{CONTACT.admin.label}</dt>
                  <dd>
                    <div className="text-[16px] text-[var(--site-ink)]">
                      {CONTACT.admin.street}
                      <br />
                      {CONTACT.admin.postal}
                    </div>
                    <a href={`mailto:${CONTACT.admin.email}`} className="site-ul mt-2 inline-block text-[14px] font-medium text-[var(--site-ink)]">
                      {CONTACT.admin.email}
                    </a>
                  </dd>
                </Reveal>
                <Reveal as="div" delay={3} className="grid gap-2 py-6 sm:grid-cols-[200px_1fr]" data-testid="contact-people">
                  <dt className="text-[14px] font-medium text-[var(--hr-muted)]">Kontaktpersoner</dt>
                  <dd>
                    <ul className="grid gap-5 sm:grid-cols-2">
                      {CONTACT.people.map((p) => (
                        <li key={p.name}>
                          <div className="text-[16px] font-medium text-[var(--site-ink)]">{p.name}</div>
                          <div className="text-[13.5px] text-[var(--hr-muted)]">{p.title}</div>
                          {p.phone && (
                            <a href={p.phoneHref} className="hr-tabular site-ul mt-1.5 inline-block text-[14px] font-medium text-[var(--site-ink)]">
                              {p.phone}
                            </a>
                          )}
                          {p.email && (
                            <a href={`mailto:${p.email}`} className="site-ul mt-1.5 inline-block text-[14px] font-medium text-[var(--site-ink)]">
                              {p.email}
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </Reveal>
              </dl>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
